// BattlemapsPricing.js
// Battlemaps pricing: explain and compare what you can buy.
//
// Pricing has ONE job. It is not the Environment Pack shop — the full catalogue
// lives at /battlemaps/packs, and rendering all of it here is what made this
// page a scrolling list instead of an explanation. Here you get the Environment
// Pack proposition stated once, a single row of representative examples, and
// every Creation Credit pack.
//
// Every price and quantity comes from the authoritative commerce configuration
// (GET /v1/battlemaps/commerce via useBattlemapsCommerce). Nothing commercial is
// re-declared in this file.
import React, { useMemo, useState } from 'react';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import {
  useBattlemapsCommerce, useBattlemapsAccount, useBattlemapsPacks, formatPrice,
} from './battlemapsCommerce';

// The eight Creation Credit products render as 4 x 2. auto-fill previously
// derived the column count from the available width and produced 5 + 3, which
// reads as "five products, plus three left over". Four is pinned here instead,
// and only relaxed at genuinely narrow widths. A scoped stylesheet is used
// because inline styles cannot carry a media query.
const CREDIT_GRID_CSS = `
.vi-bm-credit-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
  margin-top: 4px;
}
@media (max-width: 1024px) { .vi-bm-credit-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 560px)  { .vi-bm-credit-grid { grid-template-columns: minmax(0, 1fr); } }

/* Four Environment Pack examples in ONE row at desktop and laptop. Same
   reasoning as the credit grid: the column count is pinned rather than derived
   from available width, so the row cannot silently become 3 + 1. */
.vi-bm-example-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  align-items: stretch;
  margin-top: 6px;
}
@media (max-width: 1024px) { .vi-bm-example-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 560px)  { .vi-bm-example-row { grid-template-columns: minmax(0, 1fr); } }
`;

const AMBER = '#d4a72c';
const CARD_BORDER = '1px solid rgba(212,167,44,0.22)';

// How many example packs the row shows. Deliberately a handful, not a catalogue.
const EXAMPLE_COUNT = 4;

export default function BattlemapsPricing() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const { commerce } = useBattlemapsCommerce();
  const { ownedSlugs, creationCredits } = useBattlemapsAccount();
  // The catalogue comes from the published pack layer, so the pack count and the
  // examples below cannot go stale when the pipeline publishes a new pack.
  const { packs } = useBattlemapsPacks();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const env = commerce.environment_pack;

  // One representative pack per environment group first, so the row shows the
  // BREADTH of the catalogue rather than four near-identical forest packs.
  const examples = useMemo(() => {
    const seen = new Set();
    const picked = [];
    packs.forEach((p) => {
      if (picked.length >= EXAMPLE_COUNT || seen.has(p.category)) return;
      seen.add(p.category);
      picked.push(p);
    });
    for (const p of packs) {
      if (picked.length >= EXAMPLE_COUNT) break;
      if (!picked.includes(p)) picked.push(p);
    }
    return picked;
  }, [packs]);

  const buyPack = async (slug) => {
    setBusy(slug); setError('');
    try {
      const r = await API.post(`/v1/battlemaps/packs/${slug}/checkout`);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const d = err.response && err.response.data;
      setError((d && (d.message || d.error)) || 'Checkout could not be started. Please try again.');
      setBusy(null);
    }
  };

  const buyCredits = async (key) => {
    setBusy(key); setError('');
    try {
      // The route reads `topup_key`; anything else resolves to no product.
      const r = await consentCheckout.start(() => API.post('/v1/battlemaps/generations/checkout', { topup_key: key }), { purchaseType: 'battlemap_generation_topup', intendedSelector: key });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const d = err.response && err.response.data;
      setError((d && (d.message || d.error)) || 'Checkout could not be started. Please try again.');
      setBusy(null);
    }
  };

  return (
    <>
      <style>{CREDIT_GRID_CSS}</style>
      <ProductNav />
      <div style={s.page}>
        <div style={s.wrap}>

          <header style={s.head}>
            <div style={s.eyebrow}>Battlemaps</div>
            <h1 style={s.h1}>Pricing</h1>
            <p style={s.lead}>
              Two things to buy: environments, which you own permanently, and Creation
              Credits, which you spend when you generate.
            </p>
          </header>

          {error ? <div style={s.error}>{error}</div> : null}

          {/* ── Environment Packs: stated once ── */}
          <section style={s.section}>
            <h2 style={s.h2}>Environment Packs</h2>
            <p style={s.body}>
              <strong style={s.strong}>{formatPrice(env.price_usd, commerce.currency)}, one-time.</strong>{' '}
              Each pack permanently unlocks one environment across all five Battlemaps
              creation systems — {env.unlocks_engines.join(', ')} — and includes{' '}
              <strong style={s.strong}>{env.included_credits} Creation Credits</strong>.
              There is no subscription and packs do not expire.
            </p>

            <div className="vi-bm-example-row">
              {examples.map((p) => {
                const owned = ownedSlugs.has(p.id);
                return (
                  <article key={p.id} style={s.exCard}>
                    <GuardedCheckoutConsent checkout={consentCheckout} />
                    {/* Reserved-height regions: a two-line pack name must not
                        push the price, feature list or button down relative to
                        its neighbours. */}
                    <div style={s.exOwnedRow}>
                      {owned ? <span style={s.ownedBadge}>Owned</span> : null}
                    </div>
                    <div style={s.exName}>{p.name}</div>
                    <div style={s.exCatStrip}>
                      {p.categoryName}
                      {p.generatorCount ? ` \u2022 ${p.generatorCount} generators` : ''}
                    </div>
                    <div style={s.exPriceRow}>
                      <span style={s.exCurrency}>$</span>
                      <span style={s.exPrice}>{Math.round(env.price_usd)}</span>
                      <span style={s.exUsd}>{commerce.currency}</span>
                    </div>
                    <div style={s.exTerms}>One-time purchase &bull; Permanent ownership</div>
                    <div style={s.exDivider} />
                    <div style={s.exFeatures}>
                      {env.unlocks_engines.map((engine) => (
                        <div key={engine} style={s.exFeature}>
                          <span style={s.check}>&#10003;</span>{engine}
                        </div>
                      ))}
                      {/* The five checks above already say what the buyer
                          receives. An "unlocks generators" line restates it, and
                          expiry policy belongs in the Creation Credits section,
                          not on a $45 product card. */}
                      <div style={s.exFeatureGold}>
                        <span style={s.goldDot} />
                        {env.included_credits} Creation Credits included
                      </div>
                    </div>
                    <button
                      type="button"
                      style={owned ? s.exBtnOwned : s.exBtn}
                      disabled={owned || busy === p.id}
                      onClick={() => (owned ? null : buyPack(p.id))}
                    >
                      {owned ? 'Owned' : (busy === p.id ? 'Starting\u2026' : 'Purchase Environment Pack')}
                    </button>
                  </article>
                );
              })}
            </div>

            <div style={s.viewAllRow}>
              <button type="button" style={s.viewAllBtn} onClick={() => navigate('/battlemaps/packs')}>
                View All Environment Packs
              </button>
              <span style={s.viewAllHint}>
                {packs.length} packs available
              </span>
            </div>
          </section>

          {/* ── Creation Credits: what they are ── */}
          <section style={s.section}>
            <h2 style={s.h2}>Creation Credits</h2>
            <p style={s.body}>
              Creation Credits are the single currency across every Battlemaps system.
              They arrive with each Environment Pack and can be topped up any time.
              They work across every environment you own and never expire.
            </p>
            <p style={s.body}>
              Different outputs consume different amounts. A still map costs less than a
              Cinematic video; within Cinematic, higher quality costs more, and a longer
              clip costs more than a short one. Selecting Loop does not add a surcharge.
              The exact cost is always shown before you generate, and again on the
              confirmation screen.
            </p>
            {creationCredits > 0 && (
              <div style={s.balancePill}>Your balance: {creationCredits} Creation Credits</div>
            )}

            <div className="vi-bm-credit-grid">
              {commerce.credit_packs.map((p) => (
                <article key={p.key} style={s.crCard}>
                  <div style={s.crName}>{p.name}</div>
                  <div style={s.crCredits}>
                    <span style={s.crCreditsNum}>{p.credits}</span>
                    <span style={s.crCreditsUnit}>credits</span>
                  </div>
                  <div style={s.crPrice}>{formatPrice(p.price_usd, commerce.currency)}</div>
                  <p style={s.crBlurb}>{p.blurb || ' '}</p>
                  <button
                    type="button"
                    style={s.crBtn}
                    disabled={busy === p.key}
                    onClick={() => buyCredits(p.key)}
                  >
                    {busy === p.key ? 'Starting…' : 'Buy credits'}
                  </button>
                </article>
              ))}
            </div>
            {commerce.credit_packs.length === 0 && (
              <div style={s.body}>Credit packs are loading…</div>
            )}
          </section>

        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles.
//
// Both card grids use fixed-height regions for name / value / price / helper so
// equivalent cards align internally, and pin the action with marginTop:auto so
// buttons sit on one line regardless of description length.
// ---------------------------------------------------------------------------
const s = {
  // Standalone Battlemaps pages paint their own ground. Without this the
  // header sat on the app's light default and the title washed out.
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #100e09 0%, #17150f 100%)', padding: '28px 20px 60px', boxSizing: 'border-box' },
  wrap: { maxWidth: 1240, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 },

  head: { display: 'flex', flexDirection: 'column', gap: 8 },
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: AMBER },
  h1: { margin: 0, fontWeight: 800, fontSize: 'clamp(26px, 2.6vw, 36px)', color: '#f4f4f8', lineHeight: 1.12 },
  lead: { margin: 0, fontSize: 'clamp(14px, 1.15vw, 17px)', color: 'rgba(255,255,255,0.66)', lineHeight: 1.6, maxWidth: 720 },

  /* Middle layer of the hierarchy: page (darkest) -> section -> product card. */
  section: { background: 'linear-gradient(158deg, rgba(212,167,44,0.10) 0%, rgba(212,167,44,0.035) 60%, rgba(255,255,255,0.022) 100%), #1a1710', border: CARD_BORDER, borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 12px 34px rgba(0,0,0,0.30)' },
  h2: { margin: 0, fontWeight: 800, fontSize: 'clamp(19px, 1.8vw, 24px)', color: '#f4f4f8' },
  body: { margin: 0, fontSize: 'clamp(13.5px, 1.08vw, 15.5px)', color: 'rgba(255,255,255,0.66)', lineHeight: 1.65, maxWidth: 860 },
  strong: { color: '#f0e3bd' },
  error: { background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.4)', color: '#fca5a5', borderRadius: 10, padding: '12px 16px', fontSize: 14 },
  balancePill: { alignSelf: 'flex-start', fontSize: 12.5, fontWeight: 700, color: '#f0e3bd', background: 'rgba(212,167,44,0.12)', border: '1px solid rgba(212,167,44,0.3)', borderRadius: 999, padding: '6px 15px' },

  // ONE row. auto-fit + minmax means 3-4 across depending on width, never a grid.
  /* Light product card against the dark page — the hierarchy the page needs is
     page -> section panel -> clearly distinct product, not three shades of the
     same dark. */
  exCard: { background: '#fbfaf7', border: '1px solid rgba(212,175,55,0.55)', borderTop: '4px solid #D4AF37', borderRadius: 14, padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', boxShadow: '0 10px 26px rgba(0,0,0,0.35)' },
  exOwnedRow: { height: 24, display: 'flex', alignItems: 'center' },
  ownedBadge: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#fff', background: 'linear-gradient(135deg, #15803d, #16a34a)', borderRadius: 20, padding: '3px 12px' },
  /* Two lines reserved: a wrapping name must not shift the rest of the card. */
  exName: { fontWeight: 800, fontSize: 17, color: '#1a1a1a', lineHeight: 1.22, height: 42, overflow: 'hidden' },
  exCatStrip: { marginTop: 8, background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, lineHeight: 1.35, height: 34, boxSizing: 'border-box', overflow: 'hidden' },
  exPriceRow: { display: 'flex', alignItems: 'baseline', gap: 2, marginTop: 12, height: 40 },
  exCurrency: { fontSize: 16, fontWeight: 700, color: '#1a1a1a', alignSelf: 'flex-start', marginTop: 6 },
  exPrice: { fontSize: 36, fontWeight: 800, color: '#1a1a1a', lineHeight: 1 },
  exUsd: { fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.05em', marginLeft: 3 },
  exTerms: { fontSize: 11.5, color: '#6b7280', fontWeight: 600, height: 18 },
  exDivider: { height: 1, background: '#ececec', margin: '10px 0' },
  exFeatures: { display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 16 },
  exFeature: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#374151', fontWeight: 500 },
  exFeatureGold: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#92400e', fontWeight: 700 },
  exFeatureMuted: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#6b7280', fontWeight: 500 },
  check: { color: '#16a34a', fontWeight: 800, fontSize: 12.5, width: 12, textAlign: 'center', flexShrink: 0 },
  goldDot: { width: 7, height: 7, borderRadius: '50%', background: '#D4AF37', flexShrink: 0, marginLeft: 2, marginRight: 3 },
  exBtn: { ...glassSurface('#A66D05', '#D4AF37', '#F0D174'), marginTop: 'auto', borderRadius: 10, padding: '13px 0', fontWeight: 700, fontSize: 13.5, width: '100%', cursor: 'pointer' },
  exBtnOwned: { ...glassSurface('#0E7A50', '#16a34a', '#4ADE80'), marginTop: 'auto', borderRadius: 10, padding: '13px 0', fontWeight: 700, fontSize: 13.5, width: '100%', cursor: 'default' },

  viewAllRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 4 },
  viewAllBtn: { ...glassSurface('#A66D05', '#D4AF37', '#F0D174'), borderRadius: 999, padding: '11px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  viewAllHint: { fontSize: 13, color: 'rgba(255,255,255,0.45)' },

  /* creditGrid now lives in CREDIT_GRID_CSS so it can carry media queries. */
  /* Lifted clear of the section panel: lighter surface, gold top edge and a
     shadow, so each reads as an individual purchasable product rather than a
     subdivision of the panel behind it. */
  crCard: { background: viGlassFill('#8A6D14', '#d4a72c', '#E8C65A'), border: viGlassBorder('#E8C65A', 1.5), boxShadow: viGlassShadow('#8A6D14', '#d4a72c', '#E8C65A', false), borderTop: '3px solid rgba(212,167,44,0.85)', borderRadius: 12, padding: 22, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' },
  crName: { fontWeight: 800, fontSize: 15.5, color: '#ffffff', height: 20, letterSpacing: '0.01em' },
  crCredits: { display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10, height: 38 },
  crCreditsNum: { fontSize: 30, fontWeight: 900, color: AMBER, lineHeight: 1 },
  crCreditsUnit: { fontSize: 12.5, color: 'rgba(255,255,255,0.5)', fontWeight: 600 },
  crPrice: { fontSize: 18, fontWeight: 800, color: '#ffffff', height: 24, marginTop: 4 },
  crBlurb: { margin: '6px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.68)', lineHeight: 1.5, minHeight: 42 },
  crBtn: { ...glassSurface('#A66D05', '#D4AF37', '#F0D174'), marginTop: 'auto', borderRadius: 999, padding: '10px 0', fontWeight: 800, fontSize: 13, width: '100%', cursor: 'pointer' },
};
